import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { ArrowLeft, Loader2, AlertTriangle } from 'lucide-react'
import {
  useLibro,
  useCreateLibro,
  useUpdateLibro,
  useCampos,
  useCreateCampo,
  useUpdateCampo,
  useDeleteCampo,
} from '../../hooks/useQrCodigo'
import { CamposManager } from '../../components/CamposManager'
import type { LibroIn } from '../../types'

export function LibroFormPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditing = !!id
  const libroId = id ? parseInt(id) : undefined

  const { data: libro, isLoading: libroLoading } = useLibro(libroId)
  const { data: campos = [] } = useCampos(libroId)
  const createMutation = useCreateLibro()
  const updateMutation = useUpdateLibro()
  const createCampoMutation = useCreateCampo()
  const updateCampoMutation = useUpdateCampo()
  const deleteCampoMutation = useDeleteCampo()

  const [form, setForm] = useState<LibroIn>({
    nombre: '',
    descripcion: '',
    archivo_url: '',
    imagen_portada_url: null,
    activo: true,
    limite_descargas_por_ip: 0,
    requiere_formulario: true,
    permitir_solicitud_codigo: true,
    fecha_inicio: null,
    fecha_fin: null,
    autor: null,
    editorial: null,
    isbn: null,
    paginas: null,
  })

  useEffect(() => {
    if (libro) {
      setForm({
        nombre: libro.nombre,
        descripcion: libro.descripcion || '',
        archivo_url: libro.archivo_url,
        imagen_portada_url: libro.imagen_portada_url,
        activo: libro.activo,
        limite_descargas_por_ip: libro.limite_descargas_por_ip,
        requiere_formulario: libro.requiere_formulario,
        permitir_solicitud_codigo: libro.permitir_solicitud_codigo,
        fecha_inicio: libro.fecha_inicio,
        fecha_fin: libro.fecha_fin,
        autor: libro.autor,
        editorial: libro.editorial,
        isbn: libro.isbn,
        paginas: libro.paginas,
      })
    }
  }, [libro])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.nombre || !form.archivo_url) return

    if (isEditing && libroId) {
      await updateMutation.mutateAsync({ id: libroId, payload: form })
      navigate(`/libro-qr/${libroId}`)
    } else {
      const created = await createMutation.mutateAsync(form)
      navigate(`/libro-qr/${created.id}`)
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending

  if (isEditing && libroLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">
          {isEditing ? 'Editar Libro' : 'Crear Libro'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Info Básica */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg">Información Básica</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Nombre del libro *</Label>
              <Input
                value={form.nombre}
                onChange={(e) => setForm(p => ({ ...p, nombre: e.target.value }))}
                placeholder="Nombre del libro"
                required
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <textarea
                className="w-full border rounded-md p-2 text-sm min-h-[80px] bg-background"
                value={form.descripcion}
                onChange={(e) => setForm(p => ({ ...p, descripcion: e.target.value }))}
                placeholder="Descripción del libro"
              />
            </div>
            <div>
              <Label>URL del archivo (PDF/Drive) *</Label>
              <Input
                value={form.archivo_url}
                onChange={(e) => setForm(p => ({ ...p, archivo_url: e.target.value }))}
                placeholder="https://drive.google.com/..."
                required
              />
            </div>
            <div>
              <Label>URL de imagen de portada</Label>
              <Input
                value={form.imagen_portada_url || ''}
                onChange={(e) => setForm(p => ({ ...p, imagen_portada_url: e.target.value || null }))}
                placeholder="https://..."
              />
              {form.imagen_portada_url && (
                <img
                  src={form.imagen_portada_url}
                  alt="Preview"
                  className="mt-2 h-32 rounded-md object-cover"
                  onError={(e) => (e.currentTarget.style.display = 'none')}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Configuración */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg">Configuración</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <Switch
                checked={form.activo}
                onCheckedChange={(checked) => setForm(p => ({ ...p, activo: checked }))}
                className={form.activo ? 'data-[state=checked]:bg-teal-500' : ''}
              />
              <Label>Activo</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.requiere_formulario}
                onCheckedChange={(checked) => setForm(p => ({ ...p, requiere_formulario: checked }))}
                className={form.requiere_formulario ? 'data-[state=checked]:bg-teal-500' : ''}
              />
              <Label>Requiere formulario antes de descarga</Label>
            </div>
            {!form.requiere_formulario && (
              <div className="flex items-center gap-2 text-amber-600 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-md">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <p className="text-sm">Sin formulario, el usuario descargará directamente al validar el código.</p>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Switch
                checked={form.permitir_solicitud_codigo}
                onCheckedChange={(checked) => setForm(p => ({ ...p, permitir_solicitud_codigo: checked }))}
                className={form.permitir_solicitud_codigo ? 'data-[state=checked]:bg-teal-500' : ''}
              />
              <Label>Permitir solicitar código cuando ya fue canjeado</Label>
            </div>
            <div>
              <Label>Límite de descargas por IP (0 = ilimitado)</Label>
              <Input
                type="number"
                value={form.limite_descargas_por_ip}
                onChange={(e) => setForm(p => ({ ...p, limite_descargas_por_ip: parseInt(e.target.value) || 0 }))}
                min={0}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha de inicio</Label>
                <Input
                  type="datetime-local"
                  value={form.fecha_inicio ? form.fecha_inicio.slice(0, 16) : ''}
                  onChange={(e) => setForm(p => ({ ...p, fecha_inicio: e.target.value ? new Date(e.target.value).toISOString() : null }))}
                />
              </div>
              <div>
                <Label>Fecha de fin</Label>
                <Input
                  type="datetime-local"
                  value={form.fecha_fin ? form.fecha_fin.slice(0, 16) : ''}
                  onChange={(e) => setForm(p => ({ ...p, fecha_fin: e.target.value ? new Date(e.target.value).toISOString() : null }))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Campos del formulario (solo en edición) */}
        {isEditing && libroId && (
          <Card className="border border-gray-300 dark:border-gray-700">
            <CardHeader>
              <CardTitle className="text-lg">Campos del Formulario</CardTitle>
            </CardHeader>
            <CardContent>
              <CamposManager
                campos={campos}
                onCreateCampo={(data) => createCampoMutation.mutate({ libroId, payload: data })}
                onUpdateCampo={(campoId, data) => updateCampoMutation.mutate({ libroId, campoId, payload: data })}
                onDeleteCampo={(campoId) => deleteCampoMutation.mutate({ libroId, campoId })}
              />
            </CardContent>
          </Card>
        )}

        {/* Metadata */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg">Metadata (opcional)</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <div>
              <Label>Autor</Label>
              <Input
                value={form.autor || ''}
                onChange={(e) => setForm(p => ({ ...p, autor: e.target.value || null }))}
                placeholder="Autor del libro"
              />
            </div>
            <div>
              <Label>Editorial</Label>
              <Input
                value={form.editorial || ''}
                onChange={(e) => setForm(p => ({ ...p, editorial: e.target.value || null }))}
                placeholder="Editorial"
              />
            </div>
            <div>
              <Label>ISBN</Label>
              <Input
                value={form.isbn || ''}
                onChange={(e) => setForm(p => ({ ...p, isbn: e.target.value || null }))}
                placeholder="ISBN"
              />
            </div>
            <div>
              <Label>Páginas</Label>
              <Input
                type="number"
                value={form.paginas || ''}
                onChange={(e) => setForm(p => ({ ...p, paginas: e.target.value ? parseInt(e.target.value) : null }))}
                placeholder="Número de páginas"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancelar
          </Button>
          <Button type="submit" className="bg-teal-800 hover:bg-teal-900 text-white" disabled={isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditing ? 'Guardar cambios' : 'Crear libro'}
          </Button>
        </div>
      </form>
    </div>
  )
}
